import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { recipeDetailQuery, recipesKeys, recipeSnapshotQuery, useRecipe, useRecipeSnapshot, type RecipeDetail } from '@/api/queries/recipes';
import { RecipeEditor } from '@/features/recipes/editor/RecipeEditor';
import { isRecipeMissing, RecipeLoadError, RecipeNotFound, RecipeVersionGone } from '@/features/recipes/RecipeMissing';

type Search = { version?: number };

export const Route = createFileRoute('/_app/recipes/$id/')({
  validateSearch: (s: Record<string, unknown>): Search => {
    const v = Number(s.version);
    return { version: Number.isInteger(v) && v > 0 ? v : undefined };
  },
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, params, deps }) => {
    const id = Number(params.id);
    await Promise.all([
      context.queryClient.ensureQueryData(recipeDetailQuery(id)),
      deps.version ? context.queryClient.ensureQueryData(recipeSnapshotQuery(id, deps.version)) : Promise.resolve(null),
    ]);
  },
  // Only a 404 (or a link that is not an id) is "not found". A 502 during a
  // deploy, or the wifi dropping out on the pass, must not tell the chef the
  // recipe was deleted: that says so and offers Try again.
  errorComponent: EditRecipeError,
  component: EditRecipe,
});

function EditRecipeError({ error }: { error: unknown }) {
  const router = useRouter();
  const qc = useQueryClient();
  const { id } = Route.useParams();
  const { version } = Route.useSearch();
  if (!isRecipeMissing(error)) return <RecipeLoadError error={error} onRetry={() => router.invalidate()} />;
  // A ?version= that is gone while the recipe itself loaded: the version is
  // missing, not the recipe.
  const live = qc.getQueryData<RecipeDetail>(recipesKeys.detail(Number(id)));
  if (version && live && !live.deleted_at) return <RecipeVersionGone recipe={live} current={`/recipes/${live.id}`} />;
  return <RecipeNotFound />;
}

function EditRecipe() {
  const { id } = Route.useParams();
  const { version } = Route.useSearch();
  const recipeId = Number(id);
  const live = useRecipe(recipeId);
  const snap = useRecipeSnapshot(recipeId, version ?? null);
  if (!live.data) return null;
  if (version && !snap.data) return null;
  return (
    <RecipeEditor
      key={`${recipeId}:${version ?? 'live'}`}
      detail={live.data}
      snapshot={version ? (snap.data ?? null) : null}
      snapId={version ?? null}
    />
  );
}
